import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { useLang, pick } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useSite } from "@/lib/site";
import { fileUrl } from "@/lib/api";
import { useContact, DirectionsButton } from "@/components/site/Contact";
import { MapPin } from "lucide-react";

export default function Hero() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { site } = useSite();
  const hero = site?.photos?.find((p) => p.is_hero) || site?.photos?.[0];
  const c = useContact();
  const nav = useNavigate();
  const go = () => {
    if (user) return nav(user.role === "admin" ? "/admin" : "/member");
    document.getElementById("memberships")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="grain relative overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 20% 15%, rgba(229,9,20,0.20) 0%, rgba(8,8,10,0) 55%)" }} />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7">
          <div className="reveal inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-950/50 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-red-300">
            <span className="rec-dot" /> {t("hero.live")}
          </div>
          <h1 className="reveal mt-6 font-display text-6xl uppercase leading-[0.9] sm:text-7xl lg:text-8xl" style={{ animationDelay: "0.1s" }}>
            <span className="chrome-text block">{t("hero.title1")}</span>
            <span className="chrome-text block">{t("hero.title2")}</span>
            <span className="block text-[#ff1e27]">{t("hero.title3")}</span>
          </h1>
          <p className="reveal mt-6 max-w-xl text-base text-slate-400 md:text-lg" style={{ animationDelay: "0.2s" }}>{t("hero.sub")}</p>
          <div className="reveal mt-8 flex flex-wrap gap-3" style={{ animationDelay: "0.3s" }}>
            <a href="#memberships" data-testid="hero-become-member-btn" className="btn-red rounded-full px-6 py-3 font-semibold">{t("nav.member")}</a>
            <button onClick={go} data-testid="hero-book-session-btn" className="btn-chrome rounded-full px-6 py-3 font-semibold">{t("nav.book")}</button>
          </div>
          <div className="reveal mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center" style={{ animationDelay: "0.4s" }}>
            <a href={c.maps_link} target="_blank" rel="noopener noreferrer" data-testid="hero-address-link"
              className="inline-flex items-center gap-2 text-lg font-medium text-white underline-offset-4 hover:underline">
              <MapPin size={18} className="text-red-500" /> {c.address}
            </a>
            <a href={`tel:${c.phone_tel}`} data-testid="hero-phone-link" className="inline-flex items-center gap-2 font-mono text-lg text-white hover:text-red-400">
              <Phone size={18} className="text-red-500" /> {c.phone_display}
            </a>
          </div>
          <div className="reveal mt-5 flex flex-wrap gap-3" style={{ animationDelay: "0.5s" }}>
            <DirectionsButton id="hero" />
            <a href={`tel:${c.phone_tel}`} data-testid="hero-call-btn" className="btn-chrome inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium">
              <Phone size={16} /> {t("hero.call")}
            </a>
          </div>
        </div>
        <div className="reveal lg:col-span-5" style={{ animationDelay: "0.25s" }}>
          <div className="brackets panel overflow-hidden p-2">
            {hero ? (
              <img src={fileUrl(hero.storage_path)} alt={pick(hero, "caption", lang) || "RECORDING ROOM"} width={hero.width} height={hero.height}
                fetchPriority="high" decoding="async" className="block h-auto max-h-[78vh] w-full rounded-xl object-contain" data-testid="hero-photo" />
            ) : (
              <img src="/brand/logo.jpg" alt="RECORDING ROOM logo" className="w-full rounded-xl" data-testid="hero-logo" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
